import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LifeBuoy, Paperclip, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const CATEGORIES = [
  { value: 'bug', label: 'Something is broken' },
  { value: 'billing', label: 'Billing question' },
  { value: 'onboarding', label: 'Getting started' },
  { value: 'feature_request', label: 'Feature request' },
  { value: 'account', label: 'My account' },
  { value: 'other', label: 'Other' },
];

/** Spec Section 12 — one component doubles as "report a problem" and
 * "feedback" via a type toggle, available on every customer-facing page. */
export default function HelpFeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('problem');
  const [category, setCategory] = useState('bug');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleScreenshotSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setScreenshotPreview(URL.createObjectURL(file));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/support/tickets/screenshot`, formData, {
        headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' },
      });
      setScreenshotUrl(data.screenshot_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not attach screenshot');
      setScreenshotPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const clearScreenshot = () => {
    setScreenshotUrl(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submit = async () => {
    if (!description.trim()) {
      toast.error('Tell us a bit more first');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/support/tickets`, {
        ticket_type: type, category, description: description.trim(), page: window.location.pathname,
        screenshot_url: screenshotUrl,
      }, authHeaders());
      toast.success(type === 'feedback' ? 'Thanks for the feedback!' : "Got it - we're on it.");
      setDescription('');
      clearScreenshot();
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send - try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 left-6 z-40 rounded-full bg-secondary text-secondary-foreground shadow-lg p-3.5 hover:scale-105 transition-transform"
        data-testid="help-feedback-fab"
        title="Get help or share feedback"
      >
        <LifeBuoy className="h-5 w-5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="help-feedback-modal">
          <DialogHeader>
            <DialogTitle>Get help or share feedback</DialogTitle>
            <DialogDescription>Report a problem or share feedback — our team sees it right away.</DialogDescription>
          </DialogHeader>

          <div className="flex rounded-lg border overflow-hidden mb-2">
            {[{ k: 'problem', label: 'Report a problem' }, { k: 'feedback', label: 'Share feedback' }].map((t) => (
              <button
                key={t.k}
                onClick={() => setType(t.k)}
                data-testid={`help-feedback-type-${t.k}`}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${type === t.k ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/70'}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="space-y-3">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="help-feedback-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={type === 'feedback' ? "What's on your mind?" : 'What happened? What did you expect instead?'}
              rows={5}
              data-testid="help-feedback-description-input"
            />
            {type === 'problem' && (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={handleScreenshotSelect}
                  data-testid="help-feedback-screenshot-input"
                />
                {screenshotPreview ? (
                  <div className="relative inline-block">
                    <img src={screenshotPreview} alt="Screenshot preview" className="h-16 rounded border" />
                    {uploading && <span className="absolute inset-0 flex items-center justify-center bg-background/70 text-xs">Uploading…</span>}
                    <button
                      onClick={clearScreenshot}
                      className="absolute -top-2 -right-2 rounded-full bg-destructive text-destructive-foreground p-0.5"
                      data-testid="help-feedback-screenshot-remove-btn"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="help-feedback-screenshot-attach-btn"
                  >
                    <Paperclip className="h-3.5 w-3.5" /> Attach a screenshot (optional)
                  </button>
                )}
              </div>
            )}
            <Button onClick={submit} disabled={submitting || uploading} className="w-full" data-testid="help-feedback-submit-btn">
              {submitting ? 'Sending...' : type === 'feedback' ? 'Send feedback' : 'Send report'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
